/**
 * FileButtons Component
 *
 * Collapsible file buttons for changed files in agent sessions.
 * Shows a badge when collapsed, expands to show recent files.
 *
 * @module components/session/FileButtons
 */

import { memo, useState, useCallback, useEffect, useRef } from 'react';
import { FileText, FilePlus, Pencil, Loader2, ChevronDown, ChevronUp, FolderOpen, AlertTriangle } from 'lucide-react';

const MAX_VISIBLE_FILES = 5;
const MAX_SCROLL_HEIGHT = 300; // px

/**
 * Format relative time (e.g., "5 mins ago")
 */
function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Single file button
 */
const FileButton = memo(function FileButton({ file, onClick, isLoading, showTime = false, exists = true }) {
  const Icon = file.type === 'edit' ? Pencil : FilePlus;
  const isDisabled = isLoading || exists === false;

  // Different styles for existing vs deleted files
  const colorClass = exists === false
    ? 'bg-gray-700/50 border-gray-600 text-gray-500 cursor-not-allowed'
    : file.type === 'edit'
      ? 'bg-orange-500/20 border-orange-500/30 text-orange-400 hover:bg-orange-500/30'
      : 'bg-green-500/20 border-green-500/30 text-green-400 hover:bg-green-500/30';
  const relativeTime = showTime ? formatRelativeTime(file.timestamp) : '';

  return (
    <button
      onClick={() => exists !== false && onClick(file)}
      disabled={isDisabled}
      className={`
        flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium
        border transition-colors
        ${colorClass}
        ${isLoading ? 'opacity-50 cursor-wait' : ''}
      `}
      title={exists === false ? `${file.path} (deleted)` : file.path}
    >
      {isLoading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : exists === false ? (
        <AlertTriangle className="w-3 h-3" />
      ) : (
        <Icon className="w-3 h-3" />
      )}
      <span className={`max-w-[120px] truncate ${exists === false ? 'line-through' : ''}`}>{file.fileName}</span>
      {showTime && relativeTime && (
        <span className="text-gray-500 text-[10px] ml-auto">{relativeTime}</span>
      )}
    </button>
  );
});

/**
 * Collapsed badge showing file count
 */
const CollapsedBadge = memo(function CollapsedBadge({ count, editCount, writeCount, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium
                 bg-gray-700/80 border border-gray-600 text-gray-300
                 hover:bg-gray-600/80 transition-colors cursor-pointer backdrop-blur-sm"
      title="Click to expand file list"
    >
      <FolderOpen className="w-4 h-4" />
      <span>{count} files</span>
      {editCount > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
          {editCount}
        </span>
      )}
      {writeCount > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
          +{writeCount}
        </span>
      )}
      <ChevronDown className="w-3 h-3" />
    </button>
  );
});

/**
 * FileButtons container - collapsible list with "show all" option
 */
export const FileButtons = memo(function FileButtons({
  changedFiles,
  onFileClick,
  loadingFile = null,
  agentId = null,
  className = ''
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [existsMap, setExistsMap] = useState({}); // { path: boolean }
  const [isCheckingExists, setIsCheckingExists] = useState(false);
  const checkedPathsRef = useRef(new Set()); // Track which paths we've checked

  // Check file existence when dropdown expands or new files appear
  useEffect(() => {
    if (!isExpanded || !agentId || changedFiles.length === 0) return;

    // Find paths we haven't checked yet
    const uncheckedPaths = changedFiles
      .map(f => f.path)
      .filter(path => !checkedPathsRef.current.has(path));

    if (uncheckedPaths.length === 0) return;

    const checkExists = async () => {
      setIsCheckingExists(true);
      try {
        const response = await fetch(`/api/agents/${agentId}/files/check-exists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ paths: uncheckedPaths }),
        });

        if (response.ok) {
          const data = await response.json();
          // Merge new results with existing
          setExistsMap(prev => ({ ...prev, ...data }));
          // Mark these paths as checked
          uncheckedPaths.forEach(p => checkedPathsRef.current.add(p));
        }
      } catch (err) {
        console.error('Failed to check file existence:', err);
      } finally {
        setIsCheckingExists(false);
      }
    };

    checkExists();
  }, [isExpanded, agentId, changedFiles]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
    setShowAll(false); // Reset show all when collapsing
  }, []);

  const toggleShowAll = useCallback(() => {
    setShowAll(prev => !prev);
  }, []);

  if (!changedFiles || changedFiles.length === 0) return null;

  // Count edit vs write files
  const editCount = changedFiles.filter(f => f.type === 'edit').length;
  const writeCount = changedFiles.filter(f => f.type === 'write').length;

  // Sort by timestamp (most recent first)
  const sortedFiles = [...changedFiles].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

  // Get visible files based on showAll state
  const visibleFiles = isExpanded
    ? (showAll ? sortedFiles : sortedFiles.slice(0, MAX_VISIBLE_FILES))
    : [];
  const hiddenCount = changedFiles.length - MAX_VISIBLE_FILES;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {/* Collapsed state - just show badge */}
      {!isExpanded && (
        <CollapsedBadge
          count={changedFiles.length}
          editCount={editCount}
          writeCount={writeCount}
          onClick={toggleExpanded}
        />
      )}

      {/* Expanded state - show files + collapse button */}
      {isExpanded && (
        <div className="flex flex-col gap-1 p-2 rounded-lg bg-gray-800/90 border border-gray-700 backdrop-blur-sm">
          {/* Header with collapse button */}
          <button
            onClick={toggleExpanded}
            className="flex items-center justify-between gap-2 px-2 py-1 text-xs text-gray-400
                       hover:text-gray-300 transition-colors"
          >
            <span>{changedFiles.length} files changed</span>
            <ChevronUp className="w-3 h-3" />
          </button>

          {/* File buttons - scrollable when showing all */}
          <div
            className={`flex flex-col gap-1 ${showAll ? 'overflow-y-auto' : ''}`}
            style={showAll ? { maxHeight: `${MAX_SCROLL_HEIGHT}px` } : undefined}
          >
            {visibleFiles.map((file) => (
              <FileButton
                key={file.path}
                file={file}
                onClick={onFileClick}
                isLoading={loadingFile === file.path}
                showTime={true}
                exists={existsMap[file.path]}
              />
            ))}
          </div>

          {/* Show more/less toggle */}
          {hiddenCount > 0 && (
            <button
              onClick={toggleShowAll}
              className="text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1 transition-colors text-left"
            >
              {showAll ? 'Show less' : `+${hiddenCount} more files...`}
            </button>
          )}
        </div>
      )}
    </div>
  );
});

export default FileButtons;
